import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Save, Loader2 } from "lucide-react";

import { CommercialFooter } from "../components/layout/commercial-footer";

export function DeliveryForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initial state matches DB columns
  const [formData, setFormData] = useState({
    data_consegna: new Date().toISOString().split('T')[0],
    apparecchiatura: "",
    quantita: "",
    codice_magazzino: "",
    lotto_seriale: "",
    apparecchiatura_seconda: "",
    qta_seconda: "",
    codice_magazzino_seconda: "",
    lotto_seriale_seconda: "",
    paziente_ente: "",
    codice_fiscale: "",
    cliente_ente: "",
    p_iva: "",
    referente: "",
    indirizzo: "",
    citta: "",
    telefono: "",
    causale: "",
    note: "",
    email_commerciale: ""
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch("/api/check_auth.php");
        const data = await response.json();
        
        if (data.authenticated && (data.role === 'commercial' || data.role === 'admin')) {
          setIsAuthenticated(true);
        } else {
          navigate("/area-commerciale/login");
        }
      } catch (err) {
        navigate("/area-commerciale/login");
      }
    };
    checkAuth();
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await fetch("/api/submit_delivery.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        window.scrollTo(0, 0);
        // Optional: Reset form or navigate away
        // navigate("/area-commerciale");
      } else {
        setError(data.message || "Errore durante il salvataggio");
        window.scrollTo(0, 0);
      }
    } catch (err) {
      setError("Errore di connessione al server");
      window.scrollTo(0, 0);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-grow py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <Button 
            onClick={() => navigate("/area-commerciale")} 
            variant="ghost" 
            className="mb-6 hover:bg-gray-200"
          >
          <ArrowLeft className="mr-2 h-4 w-4" /> Torna alla Dashboard
        </Button>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <div className="bg-[#004080] py-6 px-8">
            <h1 className="text-2xl font-bold text-white uppercase tracking-wide">
              Modulo Consegne Apparecchiature
            </h1>
            <p className="text-blue-100 mt-2 text-sm">
              Compila tutti i campi obbligatori per registrare una nuova consegna
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-8">
            {success && (
              <div className="bg-green-50 text-green-800 p-4 rounded-lg border border-green-200 mb-6">
                <strong>Successo!</strong> La consegna è stata registrata correttamente nel database.
                <div className="mt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="bg-white border-green-300 text-green-700 hover:bg-green-50"
                    onClick={() => window.location.reload()}
                  >
                    Inserisci nuova consegna
                  </Button>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-800 p-4 rounded-lg border border-red-200 mb-6">
                <strong>Errore:</strong> {error}
              </div>
            )}

            {/* Sezione 1: Dati Consegna e Apparecchiatura */}
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">Dettagli Apparecchiatura</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="data_consegna" className="block text-sm font-medium text-gray-700 mb-1">Data Consegna *</label>
                  <input
                    id="data_consegna"
                    type="date"
                    name="data_consegna"
                    value={formData.data_consegna}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="causale" className="block text-sm font-medium text-gray-700 mb-1">Causale *</label>
                  <select
                    id="causale"
                    name="causale"
                    value={formData.causale}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                    required
                  >
                    <option value="">Seleziona...</option>
                    <option value="Prima Consegna">Prima Consegna</option>
                    <option value="Sostituzione">Sostituzione</option>
                    <option value="Ritiro">Ritiro</option>
                    <option value="Manutenzione">Manutenzione</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="apparecchiatura" className="block text-sm font-medium text-gray-700 mb-1">Apparecchiatura *</label>
                  <input
                    id="apparecchiatura"
                    type="text"
                    name="apparecchiatura"
                    value={formData.apparecchiatura}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                    required
                    placeholder="Es. Ventilatore Polmonare XYZ"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantità</label>
                  <input
                    type="text"
                    name="quantita"
                    value={formData.quantita}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Codice Magazzino</label>
                  <input
                    type="text"
                    name="codice_magazzino"
                    value={formData.codice_magazzino}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lotto / Seriale</label>
                  <input
                    type="text"
                    name="lotto_seriale"
                    value={formData.lotto_seriale}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                  />
                </div>
              </div>
            </div>

            {/* Sezione 2: Apparecchiatura Secondaria (Opzionale) */}
            <div className="space-y-6 bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">Apparecchiatura Secondaria (Opzionale)</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Apparecchiatura</label>
                  <input
                    type="text"
                    name="apparecchiatura_seconda"
                    value={formData.apparecchiatura_seconda}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantità</label>
                  <input
                    type="text"
                    name="qta_seconda"
                    value={formData.qta_seconda}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Codice Magazzino</label>
                  <input
                    type="text"
                    name="codice_magazzino_seconda"
                    value={formData.codice_magazzino_seconda}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lotto / Seriale</label>
                  <input
                    type="text"
                    name="lotto_seriale_seconda"
                    value={formData.lotto_seriale_seconda}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                  />
                </div>
              </div>
            </div>

            {/* Sezione 3: Dati Paziente/Cliente */}
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">Dati Paziente / Cliente</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paziente / Ente *</label>
                  <input
                    type="text"
                    name="paziente_ente"
                    value={formData.paziente_ente}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
                  <input
                    type="text"
                    name="codice_fiscale"
                    value={formData.codice_fiscale}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partita IVA</label>
                  <input
                    type="text"
                    name="p_iva"
                    value={formData.p_iva}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cliente / Ente (se diverso da Paziente)</label>
                  <input
                    type="text"
                    name="cliente_ente"
                    value={formData.cliente_ente}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo *</label>
                  <input
                    type="text"
                    name="indirizzo"
                    value={formData.indirizzo}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Città *</label>
                  <input
                    type="text"
                    name="citta"
                    value={formData.citta}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono *</label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referente</label>
                  <input
                    type="text"
                    name="referente"
                    value={formData.referente}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Commerciale</label>
                  <input
                    type="email"
                    name="email_commerciale"
                    value={formData.email_commerciale}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                  />
                </div>
              </div>
            </div>

            {/* Sezione 4: Note */}
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-800 border-b pb-2">Altre Informazioni</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note Aggiuntive</label>
                <textarea
                  name="note"
                  value={formData.note}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#004080] focus:border-[#004080]"
                ></textarea>
              </div>
            </div>

            {/* Pulsante Submit */}
            <div className="pt-4">
              <Button 
                type="submit" 
                className="w-full h-14 text-lg font-bold bg-[#004080] hover:bg-[#003366] text-white shadow-lg uppercase"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Salvataggio in corso...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-5 w-5" />
                    Registra Consegna
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  <CommercialFooter />
</div>
);
}
